/* eslint-disable camelcase */
import { useEffect } from 'react';
import { onGetRefreshToken, onRefreshToken } from '~/api/authentication';
import { useConfig } from '~/contexts/ConfigContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { authUser, setIsInitializedFirebaseApp, setLoggingOut } from '~/redux/modules/authentication';
import { setPostLoginRedirect } from '~/utils/authRedirect';
import { generateClientId } from '~/utils/getClientId';
import { isEmbedded, isPublicRoute } from '~/utils/helpers';
import useClearStates from './useClearStates';
import useGetAppVersion from './useGetAppVersion';
import useIsProcessing from './useIsProcessing';
import {
    establishSessionFromHost,
    listenForUnsolicitedTokens,
} from '~/utils/handoffSession';
import useWebHistory from './useWebHistory';

const useGetRefreshToken = () => {
    const { setConfig } = useConfig();
    const { isProcessing, onSetIsProcessing } = useIsProcessing(true);
    const onGetAppVersion = useGetAppVersion();
    const { isAuthed } = useAppSelector(({ authentication }) => authentication);
    const dispatch = useAppDispatch();
    const clearStates = useClearStates();
    const history = useWebHistory();

    const logoutToLogin = () => {
        dispatch(setLoggingOut());
        dispatch(
            setIsInitializedFirebaseApp({
                isInitializedFirebase: false,
            })
        );

        generateClientId();

        clearStates();

        if (!isPublicRoute()) {
            // Remember where the user was so login can return them there.
            setPostLoginRedirect(window.location.pathname + window.location.search);
            history.push('/login');
        }
    };

    /**
     * Ask the embedding host for a session.
     *
     * Only reached when the ordinary client_id refresh produced nothing, so the
     * handoff is a fallback rather than a replacement: an iframe that already
     * has its own stored session keeps using it and never troubles the host.
     *
     * @returns {Promise<Boolean>} whether a session was established
     */
    const initFromHost = async () => {
        if (!isEmbedded()) return false;

        const established = await establishSessionFromHost(setConfig);

        if (established) {
            dispatch(
                authUser({
                    isAuthed: true,
                })
            );

            await onGetAppVersion();
        }

        return established;
    };

    const init = async () => {
        let refreshed = false;

        try {
            const { response } = await onGetRefreshToken();

            if (response?.refresh_token) {
                try {
                    const { token, refresh_token, api_key } = await onRefreshToken(
                        response?.refresh_token
                    );

                    setConfig({
                        TOKEN: token,
                        REFRESH_TOKEN: refresh_token,
                        API_KEY: api_key,
                    });
                    refreshed = true;
                } catch (refreshError) {
                    // The stored refresh token is invalid/expired. Don't leave a
                    // stale isAuthed state — that lands the user on /categories
                    // with a dead session. Force a clean re-login instead.
                    console.error('Refresh token expired', refreshError);

                    // Embedded, the host can re-authenticate us without the
                    // user doing anything, so try that before giving up.
                    // Redirecting to /login inside an iframe or WebView shows a
                    // password form to someone who is already signed in to the
                    // app hosting it.
                    if (await initFromHost()) return;

                    if (isAuthed) logoutToLogin();

                    return;
                }

                await onGetAppVersion();
            } else if (!(await initFromHost()) && isAuthed) {
                logoutToLogin();
            }
        } catch (error) {
            if (error === 'Network Error') {
                await init();

                return;
            }

            console.error('Error getting refresh token', error);

            // Only treat this as an expired session when the refresh itself
            // failed — not when a post-refresh call (e.g. app version) errored
            // out on an otherwise valid, refreshed session.
            if (!refreshed && !(await initFromHost()) && isAuthed) logoutToLogin();
        } finally {
            onSetIsProcessing(false);
        }
    };

    useEffect(() => {
        // Installed before init runs. The web host mints a token as it mounts
        // the iframe and pushes it without being asked, so the listener has to
        // be in place before the first await or that token lands on no one.
        const stopListening = listenForUnsolicitedTokens(established => {
            if (!established) return;

            dispatch(
                authUser({
                    isAuthed: true,
                })
            );
        }, setConfig);

        init();

        return stopListening;
    }, []);

    return {
        isProcessing,
    };
};

export default useGetRefreshToken;
